import type { Notification } from "./types";
import { buildGalleryInviteHref, getGalleryInviteTarget } from "./gallery-invite";

/** Reads the actor's name/username from `meta_data.user` — present on
 * like/follow-style notifications, absent on e.g. blockchain-transaction ones. */
export function getNotificationActor(n: Notification): { name: string; username?: string } | null {
  const user = n.meta_data?.user;
  if (!user?.name) return null;
  return { name: user.name, username: user.username };
}

/** The backend sends an explicit `link` field when a notification should be
 * clickable — respect it literally rather than guessing routes from
 * `meta_data.target_type`/`target_id` (those models are keyed by slug/contract
 * in this app's routes, not the numeric id the API gives us). */
export function getNotificationHref(n: Notification, lang: string): string | null {
  // One documented exception to "respect `link` literally": a gallery
  // invitation is addressed by the API's own user-join endpoint, which is not a
  // page here — following it verbatim sends an unauthenticated document request
  // at the backend instead of opening the invitation. See ./gallery-invite.ts.
  const inviteTarget = getGalleryInviteTarget(n);
  if (inviteTarget) return buildGalleryInviteHref(inviteTarget, lang);

  if (!n.link) return null;
  if (/^https?:\/\//i.test(n.link)) return n.link;
  const path = n.link.startsWith("/") ? n.link : `/${n.link}`;
  return path.startsWith(`/${lang}/`) || path === `/${lang}` ? path : `/${lang}${path}`;
}
